import type { CityId, Place } from '../types';
import { CITIES } from '../lib/cities';
import { formatSince } from '../lib/trust';
import type { LiveCheck } from '../lib/data';
import './TrustBanner.css';
import { useT } from '../i18n';

interface Props {
    city: CityId;
    places: Place[];
    live: LiveCheck | null;
}

/**
 * Says out loud whose word you are taking for this city, before the user starts
 * reading listings.
 *
 * This is the correction at the heart of the rebuild: v2 rendered Hong Kong's
 * certifier-issued data and Beijing's volunteer-tagged map data identically, so
 * the second borrowed the credibility of the first.
 */
export function TrustBanner({ city, places, live }: Props) {
    const { t, tn, lang } = useT();
    const meta = CITIES[city];
    const certified = places.filter((p) => p.certState === 'current' || p.certState === 'expiring_soon' || p.certState === 'listed').length;
    const lapsed = places.filter((p) => p.certState === 'expired' || p.certState === 'delisted').length;
    const total = places.length;

    const checked = live
        ? t('banner.checked', {
            source: live.source === 'avs' ? 'AVS' : meta.sourceLabel,
            when: formatSince(live.checkedAt, t, lang.code),
        })
        : null;

    if (meta.trust === 'community') {
        return (
            <aside className="banner banner--community">
                <p>
                    <strong>{t('banner.community')}</strong>{' '}
                    {t('banner.communityBody', { count: total, source: meta.sourceLabel })}
                </p>
            </aside>
        );
    }

    return (
        <aside className={`banner ${meta.trust === 'mixed' ? 'banner--mixed' : 'banner--certified'}`}>
            <p>
                <strong>{t('banner.certified', { certified, total })}</strong>
                {t('banner.certifiedBody', { source: meta.sourceLabel })}
                {lapsed > 0 && tn('banner.lapsed', 'banner.lapsed_other', lapsed)}
                {meta.trust === 'mixed' && t('banner.mixedTail')}
            </p>
            {checked && <p className="banner__checked">{checked}</p>}
        </aside>
    );
}
